import React from "react";
import { Box, Button, CircularProgress, Divider, Paper, Stack, Typography } from "@mui/material";
import CheckRounded from "@mui/icons-material/CheckRounded";
import CommitRounded from "@mui/icons-material/CommitRounded";
import ErrorOutlineRounded from "@mui/icons-material/ErrorOutlineRounded";
import { useLang } from "../i18n";

const PHASES = new Set(["preparing", "reading_source", "preparing_files", "reading_partner_directory", "saving", "finalizing", "done"]);

export function snapshotProgressKey(job) {
  if (job?.state === "queued") return "s5.snapshotProgress.queued";
  if (job?.state === "reconciling") return "s5.snapshotProgress.reconciling";
  if (job?.state !== "running" || !PHASES.has(job?.progress?.phase) || job.progress.phase === "done") return null;
  return `s5.snapshotProgress.${job.progress.phase}`;
}

// Progress is a persisted server phase, never an estimate based on elapsed time.
export default function SnapshotExecution({ phase, job, staged, spaceId, targetStage, meta }) {
  const { t } = useLang();
  if (!phase && !staged) return null;
  const active = !staged && phase?.status === "running";
  const progressKey = active ? snapshotProgressKey(job) : null;
  const failed = phase?.status === "error";
  const uncertain = phase?.status === "unknown";
  return <Paper component="section" variant="outlined" className="sect" aria-label={t("s5.execTitle")}
    sx={{ p: { xs: 2, sm: 2.5 }, border: "1px solid var(--iio-border)", borderRadius: "var(--iio-r-lg)",
      backgroundColor: "var(--iio-bg-1)", backgroundImage: "none", boxShadow: "none", color: "var(--iio-text-1)", fontFamily: "inherit" }}>
    <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1} sx={{ mb: 2 }}>
      <Typography component="h3" sx={{ fontSize: 16, fontWeight: 600, fontFamily: "inherit" }}>{t("s5.execTitle")}</Typography>
      <Typography sx={{ fontSize: 12, color: "var(--iio-text-2)", fontFamily: "inherit", overflowWrap: "anywhere" }}>{meta}</Typography>
    </Stack>
    <Stack direction="row" spacing={1.5} alignItems="center">
      {active ? <CircularProgress size={20} aria-label={t("ph.git")} sx={{ flexShrink: 0, color: "var(--iio-info)",
        "@media (prefers-reduced-motion: reduce)": { animation: "none", "& circle": { animation: "none" } } }}/>
        : staged || phase?.status === "done" ? <CheckRounded aria-hidden="true" sx={{ color: "var(--iio-success)" }}/>
          : failed || uncertain ? <ErrorOutlineRounded aria-hidden="true" sx={{ color: failed ? "var(--iio-critical)" : "var(--iio-warning)" }}/>
            : <CommitRounded aria-hidden="true" sx={{ color: "var(--iio-text-2)" }}/>}
      <Typography sx={{ fontWeight: 600, fontFamily: "inherit", flex: 1, minWidth: 0, overflowWrap: "anywhere" }}>{t("ph.git")}</Typography>
      {(staged || phase?.status === "done") && <Typography sx={{ color: "var(--iio-text-2)", fontSize: 13, fontFamily: "inherit" }}>{t("s5.done")}</Typography>}
    </Stack>
    {progressKey && <Typography role="status" aria-live="polite" aria-atomic="true" sx={{ mt: 1.5, color: "var(--iio-text-2)", fontFamily: "inherit" }}>{t(progressKey)}</Typography>}
    {uncertain && <Typography sx={{ mt: 1.5, fontFamily: "inherit" }}>{t("batch.status.unknown")}</Typography>}
    {!staged && phase?.logs?.length > 0 && <Box sx={{ mt: 1.5 }}>{phase.logs.map((line, i) => <Typography key={i} sx={{ fontFamily: "inherit", overflowWrap: "anywhere" }}>{line}</Typography>)}</Box>}
    {phase?.error && <Typography sx={{ mt: 1.5, fontFamily: "inherit", overflowWrap: "anywhere" }}>{phase.error}</Typography>}
    {staged && <>
      <Divider sx={{ my: 2, borderColor: "var(--iio-border)" }}/>
      <Box role="status" aria-live="polite">
        <Typography sx={{ fontWeight: 600, fontFamily: "inherit" }}>{t("s5.stagedTitle")}</Typography>
        <Typography sx={{ mt: 0.5, color: "var(--iio-text-2)", fontFamily: "inherit" }}>{t("s5.stagedHint", { stage: targetStage })}</Typography>
      </Box>
      <Button component="a" variant="outlined" href={`/approvals?spaceId=${encodeURIComponent(spaceId)}`}
        sx={{ mt: 2, minHeight: 44, fontFamily: "inherit", textTransform: "none", borderColor: "var(--iio-border)", color: "var(--iio-text-1)" }}>{t("rel.open")}</Button>
    </>}
  </Paper>;
}
